import qs from 'qs';

import type {
  AttractionImageMutationResponse,
  GetAttractionResponse,
  GetAttractionStatsParams,
  GetAttractionStatsResponse,
  ListAttractionCitiesResponse,
  ListAttractionImagesResponse,
  ListAttractionOffersParams,
  ListAttractionOffersResponse,
  ListAttractionsParams,
  ListAttractionsResponse,
} from '~/types/attractionTypes';

import { json_headers, request } from './common';

type RequestOptions = {
  signal?: AbortSignal;
};

type GetAttractionOptions = RequestOptions & {
  /**
   * CDN cache-buster. When provided, appended as ?v={v} so the URL bypasses
   * any pre-mutation body cached by CloudFront. Supplied by the mutation hook
   * via sessionStorage after a successful PATCH/DELETE on the attraction.
   */
  v?: string;
};

const BASE_PATH = `${window.configs.API_HOST}/api/experiences/attractions`;

/**
 * Lists attractions (admin endpoint, includes inactive).
 */
export async function listAttractions(
  params: ListAttractionsParams,
  options: RequestOptions = {},
): Promise<ListAttractionsResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/admin?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Fetches a single attraction by ID.
 * When `v` is provided, appends it as a query string to bypass CDN cache
 * after a mutation (see attractionCacheBuster.ts).
 */
export async function getAttraction(
  attractionId: string,
  options: GetAttractionOptions = {},
): Promise<GetAttractionResponse> {
  const { v, ...requestOptions } = options;
  const query = v ? `?${qs.stringify({ v })}` : '';
  return request(`${BASE_PATH}/${attractionId}${query}`, {
    ...requestOptions,
    method: 'GET',
  });
}

/**
 * Fetches cities with attraction coverage data.
 */
export async function getAttractionCities(options: RequestOptions = {}): Promise<ListAttractionCitiesResponse> {
  return request(`${BASE_PATH}/cities`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Fetches aggregated attraction stats with optional filters.
 */
export async function getAttractionStats(
  params: GetAttractionStatsParams,
  options: RequestOptions = {},
): Promise<GetAttractionStatsResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/stats?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Fetches offers matched to an attraction.
 */
export async function getAttractionOffers(
  attractionId: string,
  params: ListAttractionOffersParams,
  options: RequestOptions = {},
): Promise<ListAttractionOffersResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/${attractionId}/offers?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Toggles attraction active/inactive visibility.
 */
export async function updateAttractionActive(
  attractionId: string,
  active: boolean,
  options: RequestOptions = {},
): Promise<void> {
  return request(`${BASE_PATH}/${attractionId}/active`, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify({ active }),
    headers: json_headers,
  });
}

/**
 * Fetches available images for an attraction from linked offers.
 */
export async function getAttractionImages(
  attractionId: string,
  options: RequestOptions = {},
): Promise<ListAttractionImagesResponse> {
  return request(`${BASE_PATH}/${attractionId}/images`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Sets the hero image for an attraction. Returns the backend's updatedAt
 * timestamp so the caller can use it as a CDN cache-buster on the next GET.
 */
export async function updateAttractionImage(
  attractionId: string,
  imageId: string,
  imageUrl: string,
  options: RequestOptions = {},
): Promise<AttractionImageMutationResponse> {
  const response: { result: AttractionImageMutationResponse } = await request(`${BASE_PATH}/${attractionId}/image`, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify({ imageId, imageUrl }),
    headers: json_headers,
  });
  return response.result;
}

/**
 * Resets the attraction hero image to auto-selected. Returns updatedAt as
 * cache-buster for the next GET.
 */
export async function resetAttractionImage(
  attractionId: string,
  options: RequestOptions = {},
): Promise<AttractionImageMutationResponse> {
  const response: { result: AttractionImageMutationResponse } = await request(`${BASE_PATH}/${attractionId}/image`, {
    ...options,
    method: 'DELETE',
  });
  return response.result;
}
